import { useEffect, useState } from 'react';
import type { MutableRefObject } from 'react';
import * as Cesium from 'cesium';
import { destroyCesiumViewer } from '../shared/utils/destroyCesiumViewer';

type Container = string | Element | { current: Element | null } | { current: Element | null | undefined };

export interface UseCesiumViewerArgs {
  container: Container;
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  enabled?: boolean;
  recreateKey?: unknown;
  ionAccessToken?: string;
  viewerOptions?: Cesium.Viewer.ConstructorOptions;
  hideBaseLayerPickerButton?: boolean;
  onViewerCreated?: (viewer: Cesium.Viewer) => void;
  onViewerDestroyed?: () => void;
}

const resolveContainer = (container: Container): string | Element | null => {
  if (typeof container === 'string') return container;
  if (typeof container === 'object' && container !== null && 'current' in container) {
    return container.current ?? null;
  }
  return container ?? null;
};

export const useCesiumViewer = ({
  container,
  viewerRef,
  enabled = true,
  recreateKey,
  ionAccessToken,
  viewerOptions,
  hideBaseLayerPickerButton = false,
  onViewerCreated,
  onViewerDestroyed,
}: UseCesiumViewerArgs) => {
  const [isViewerReady, setIsViewerReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      if (viewerRef.current) {
        destroyCesiumViewer(viewerRef.current);
        viewerRef.current = null;
      }
      setIsViewerReady(false);
      return;
    }

    const resolved = resolveContainer(container);
    if (!resolved) return;

    if (ionAccessToken) {
      Cesium.Ion.defaultAccessToken = ionAccessToken;
    }

    const viewer = new Cesium.Viewer(resolved as any, viewerOptions ?? {});

    viewerRef.current = viewer;

    if (hideBaseLayerPickerButton) {
      const baseLayerPickerButton = document.querySelector('.cesium-baseLayerPicker-button');
      if (baseLayerPickerButton) {
        (baseLayerPickerButton as HTMLElement).style.display = 'none';
      }
    }

    setIsViewerReady(true);
    onViewerCreated?.(viewer);

    return () => {
      if (viewerRef.current) {
        destroyCesiumViewer(viewerRef.current);
        viewerRef.current = null;
      }
      setIsViewerReady(false);
      onViewerDestroyed?.();
    };
    // Intentionally do not depend on `viewerOptions` to avoid re-creating the
    // viewer on every render unless the caller explicitly provides `recreateKey`.
  }, [enabled, recreateKey]);

  return { isViewerReady };
};
